<template>
  <div class="expanded-wrapper bg-white">
    <h3>{{ tableTitle }}</h3>
    <div class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th
              v-for="header in tableHeaders"
              :key="header.key"
              :class="isActiveSort(sortKey, header.key)"
              @click="applySortBy(header.key)"
            >
              <span class="header-label">
                {{ header.label }}
                <span v-if="header.sortable">
                  {{ getSortArrow(sortKey, sortOrder, header.key) }}
                </span>
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <PostDisbursementChangeRow
            v-for="(
              postDisbursementChange, index
            ) in sortedPostDisbursementChanges"
            :key="`${postDisbursementChange.payoffId}_${index}`"
            :postDisbursementChange="postDisbursementChange"
            :headers="tableHeaders"
          />
        </tbody>
      </table>
    </div>
  </div>
</template>
<script setup lang="ts">
import { onMounted, ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import PostDisbursementChangeRow from "@/components/loan/refi/PostDisbursementChangeRow.vue";
import { PayoffPostDisbursementChangeModel } from "@/models/loans";
import { TableHeader } from "@/models/table";
import {
  isActiveSort,
  getSortArrow,
  sort,
  sortBy,
} from "@/composables/tableUtils";

const props = defineProps<{
  postDisbursementChanges: Array<PayoffPostDisbursementChangeModel>;
}>();

const route = useRoute();
const router = useRouter();

const sortKey = ref<string>("");
const sortOrder = ref<number>(1);

const headers = ref<Array<TableHeader>>([
  { label: "Created Date", sortable: true, key: "createdDate", display: true },
  { label: "Updated By", sortable: true, key: "updatedBy", display: false },
  {
    label: "Post-Disbursement Change Type",
    sortable: true,
    key: "payoffDisbursementStatusReasonHumanized",
    display: false,
  },
  {
    label: "Adjusted Amount",
    sortable: true,
    key: "adjustedAmount",
    display: true,
  },
  {
    label: "Adjusted Fee Amount",
    sortable: true,
    key: "adjustedFeeAmount",
    display: true,
  },
  {
    label: "Processing Date",
    sortable: true,
    key: "processingDate",
    display: true,
  },
  {
    label: "Effective Date",
    sortable: true,
    key: "effectiveDate",
    display: true,
  },
]);

const tableTitle = computed<string>(() =>
  (props.postDisbursementChanges)?.length > 1
    ? "Post-Disbursement Changes"
    : "Post-Disbursement Change"
);

const tableHeaders = computed<Array<TableHeader>>(() =>
  headers.value.filter((t: TableHeader) => t.display)
);

const sortedPostDisbursementChanges = computed(() => {
  if (!sortKey.value) {
    return props.postDisbursementChanges; // no sorting, return original order
  }

  return sort(
    [...props.postDisbursementChanges],
    sortKey.value,
    sortOrder.value
  );
});

function applySortBy(key: string) {
  const sortByResponse = sortBy(sortKey.value, sortOrder.value, key);

  sortKey.value = sortByResponse.sortKey;
  sortOrder.value = sortByResponse.sortOrder;

  router.push({
    query: {
      ...route.query,
      postDisbursementChangeSortKey: sortKey.value,
      postDisbursementChangeSortOrder: sortOrder.value.toString(),
    },
    hash: route.hash,
  });
}

function intializeSorting() {
  const querySortKey = route.query.postDisbursementChangeSortKey as string;
  const querySortOrder = parseInt(
    route.query.postDisbursementChangeSortOrder as string,
    10
  );

  if (querySortKey) {
    sortKey.value = querySortKey;
  }

  if (
    !isNaN(querySortOrder) &&
    (querySortOrder === 1 || querySortOrder === -1)
  ) {
    sortOrder.value = querySortOrder;
  }
}

onMounted(async () => {
  intializeSorting();
});
</script>

<style scoped>
h3 {
  margin-top: 9px;
}

.expanded-wrapper {
  padding: 13px 13px;
  background-color: #fff;
}

.expanded-wrapper.bg-white {
  background-color: white;
  padding-top: 0;
}

.table-wrapper {
  overflow-x: auto;
  width: 100%;
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

thead th {
  color: #000;
  font-family: Montserrat;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  text-transform: uppercase;
  cursor: pointer;
  user-select: none;
  background-color: transparent;
  border: none;
  padding: 10px 0 10px 11px;
}

.header-label {
  white-space: nowrap;
  line-height: 1;
}

th:hover {
  background-color: #ddd;
}

tr:nth-child(even) {
  background-color: #ffffff;
}

th {
  text-align: left;
}

td:nth-child(1) {
  width: 40px;
}
</style>
